/**
 * Matter Query Keys
 *
 * Centralized query key factory for all matter-related queries.
 * Follows hierarchical key structure for efficient cache invalidation.
 */

import type { MatterFilters } from './types';

/**
 * Matter query key factory
 *
 * @example
 * ```typescript
 * // Invalidate all matter queries
 * queryClient.invalidateQueries({ queryKey: matterKeys.all })
 *
 * // Invalidate specific matter
 * queryClient.invalidateQueries({ queryKey: matterKeys.detail(matterId) })
 *
 * // Get matter overview data
 * useQuery({ queryKey: matterKeys.overview(matterId), queryFn: ... })
 * ```
 */
export const matterKeys = {
  /**
   * Root key for all matter queries
   * Use for invalidating entire matter cache
   */
  all: ['matters'] as const,

  /**
   * Base key for matter lists
   * Invalidating this will clear all list variations
   */
  lists: () => [...matterKeys.all, 'list'] as const,

  /**
   * Filtered matter list
   * @param filters - Optional filters (status, search, pagination, etc.)
   */
  list: (filters?: MatterFilters) => [...matterKeys.lists(), filters] as const,

  /**
   * Base key for matter details
   * Invalidating this clears all detail queries
   */
  details: () => [...matterKeys.all, 'detail'] as const,

  /**
   * Specific matter detail
   * @param matterId - The matter ID
   */
  detail: (matterId: number) => [...matterKeys.details(), matterId] as const,

  /**
   * Whether the firm can delete the matter, and why not (VALE-1927)
   * @param matterId - The matter ID
   */
  deletionEligibility: (matterId: number) =>
    [...matterKeys.detail(matterId), 'deletion-eligibility'] as const,

  /**
   * Matter overview data (summary cards, progress)
   * @param matterId - The matter ID
   */
  overview: (matterId: number) =>
    [...matterKeys.detail(matterId), 'overview'] as const,

  /**
   * Complete matter data (all related entities)
   * @param matterId - The matter ID
   */
  complete: (matterId: number) =>
    [...matterKeys.detail(matterId), 'complete'] as const,

  /**
   * Matter statistics/metrics
   * @param matterId - The matter ID
   */
  stats: (matterId: number) =>
    [...matterKeys.detail(matterId), 'stats'] as const,

  /**
   * Matter progress/stages
   * @param matterId - The matter ID
   */
  progress: (matterId: number) =>
    [...matterKeys.detail(matterId), 'progress'] as const,

  /**
   * Matter notes
   * @param matterId - The matter ID
   */
  notes: (matterId: number) =>
    [...matterKeys.detail(matterId), 'notes'] as const,

  /**
   * Matter summary (lightweight)
   * @param matterId - The matter ID
   */
  summary: (matterId: number) =>
    [...matterKeys.detail(matterId), 'summary'] as const,

  /**
   * Matter representatives (LPRs)
   * @param matterId - The matter ID
   */
  representatives: (matterId: number) =>
    [...matterKeys.detail(matterId), 'representatives'] as const,

  /**
   * Matter beneficiaries
   * @param matterId - The matter ID
   */
  beneficiaries: (matterId: number) =>
    [...matterKeys.detail(matterId), 'beneficiaries'] as const,

  /**
   * Matter stages/phases
   * @param matterId - The matter ID
   */
  stages: (matterId: number) =>
    [...matterKeys.detail(matterId), 'stages'] as const,

  /**
   * Matter forms summary
   * @param matterId - The matter ID
   */
  forms: (matterId: number) =>
    [...matterKeys.detail(matterId), 'forms'] as const,

  /**
   * Matter metrics (counts, values)
   * @param matterId - The matter ID
   */
  metrics: (matterId: number) =>
    [...matterKeys.detail(matterId), 'metrics'] as const,

  /**
   * Matter estate admin data (for panel)
   * @param matterId - The matter ID
   * @param tab - Optional tab name
   */
  estateAdmin: (matterId: number, tab?: string) =>
    tab
      ? (['matter-estate-admin', matterId, tab] as const)
      : (['matter-estate-admin', matterId] as const)
} as const;
